if (!customElements.get('product-media-lightbox')) {
  customElements.define(
    'product-media-lightbox',
    class ProductMediaLightbox extends HTMLElement {
      constructor() {
        super();

        this.dialog = this.querySelector('dialog');
        this.track = this.querySelector('[data-lightbox-track]');
        this.slides = Array.from(this.querySelectorAll('[data-lightbox-slide]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-lightbox-thumb]'));
        this.counter = this.querySelector('[data-lightbox-counter]');
        this.desktop = window.matchMedia('(min-width: 750px)');

        this.index = 0;
        this.zoom = { scale: 1, x: 0, y: 0, pinching: false, startDistance: 0, startScale: 1 };
        this.touch = { startX: 0, currentX: 0, lastX: 0, lastY: 0, dragging: false, lastTap: 0 };

        this.onKeydown = this.onKeydown.bind(this);
      }

      connectedCallback() {
        if (!this.dialog || !this.track || this.slides.length === 0) return;

        this.gallery = document.getElementById(this.dataset.gallery);
        if (this.gallery) this.gallery.addEventListener('click', this.onGalleryClick.bind(this));

        this.querySelectorAll('[data-lightbox-close]').forEach((button) =>
          button.addEventListener('click', () => this.close())
        );
        this.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.go(this.index - 1));
        this.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.go(this.index + 1));

        this.thumbs.forEach((thumb, position) => thumb.addEventListener('click', () => this.go(position)));

        this.dialog.addEventListener('close', this.onDialogClose.bind(this));
        this.dialog.addEventListener('click', this.onDialogClick.bind(this));

        this.slides.forEach((slide) => {
          const image = slide.querySelector('[data-lightbox-image]');
          if (!image) return;
          image.addEventListener('click', this.onImageClick.bind(this));
          image.addEventListener('mousemove', this.onImageMouseMove.bind(this));
          image.addEventListener('dragstart', (event) => event.preventDefault());
        });

        this.track.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: false });
        this.track.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
        this.track.addEventListener('touchend', this.onTouchEnd.bind(this));
      }

      /* Opening / closing */

      onGalleryClick(event) {
        if (event.target.closest('.thumbnail-list__item, .splide__arrow, .splide__pagination')) return;
        if (this.gallery.querySelector('.splide.is-dragging')) return;

        const mediaItem = event.target.closest('.product__media-item[data-media-id]');
        if (!mediaItem) return;

        const mediaId = mediaItem.dataset.mediaId.split('-').pop();
        const position = this.slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        if (position < 0) return;

        event.preventDefault();
        this.open(position);
      }

      open(position) {
        this.go(position, false);
        document.body.classList.add('product-lightbox-open');
        this.dialog.showModal();
        this.scrollThumbIntoView();
        document.addEventListener('keydown', this.onKeydown);
      }

      close() {
        this.dialog.close();
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKeydown);
        document.body.classList.remove('product-lightbox-open');
      }

      onDialogClose() {
        document.removeEventListener('keydown', this.onKeydown);
        document.body.classList.remove('product-lightbox-open');
        this.resetZoom();
        this.syncGallery();
      }

      onDialogClick(event) {
        if (this.zoom.scale > 1) return;
        if (event.target.closest('.product-lightbox__figure, button')) return;
        this.close();
      }

      onKeydown(event) {
        if (event.key === 'ArrowRight') this.go(this.index + 1);
        if (event.key === 'ArrowLeft') this.go(this.index - 1);
      }

      /* Slider */

      go(position, animate = true) {
        const next = Math.min(Math.max(position, 0), this.slides.length - 1);
        if (animate === false) this.track.style.transition = 'none';
        this.index = next;
        this.track.style.transform = `translate3d(-${next * 100}%, 0, 0)`;
        if (animate === false) {
          // force a reflow so the next move animates again
          void this.track.offsetWidth;
          this.track.style.transition = '';
        }

        this.slides.forEach((slide, slidePosition) => slide.classList.toggle('is-active', slidePosition === next));
        this.thumbs.forEach((thumb, thumbPosition) => {
          const isActive = thumbPosition === next;
          thumb.classList.toggle('is-active', isActive);
          thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        if (this.counter) this.counter.textContent = `${next + 1}/${this.slides.length}`;

        const prev = this.querySelector('[data-lightbox-prev]');
        const nextButton = this.querySelector('[data-lightbox-next]');
        if (prev) prev.disabled = next === 0;
        if (nextButton) nextButton.disabled = next === this.slides.length - 1;

        this.resetZoom();
        this.scrollThumbIntoView();
      }

      scrollThumbIntoView() {
        const thumb = this.thumbs[this.index];
        if (!thumb) return;
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }

      syncGallery() {
        const mediaId = this.slides[this.index]?.dataset.mediaId;
        if (!mediaId || !this.gallery) return;

        const target = `${this.gallery.id.replace('MediaGallery-', '')}-${mediaId}`;
        if (typeof this.gallery.setActiveMedia === 'function') this.gallery.setActiveMedia(target, false);
      }

      /* Zoom */

      currentImage() {
        return this.slides[this.index]?.querySelector('[data-lightbox-image]');
      }

      applyZoom(image = this.currentImage()) {
        if (!image) return;
        image.style.transform = `translate3d(${this.zoom.x}px, ${this.zoom.y}px, 0) scale(${this.zoom.scale})`;
        image.classList.toggle('is-zoomed', this.zoom.scale > 1);
      }

      resetZoom() {
        this.zoom.scale = 1;
        this.zoom.x = 0;
        this.zoom.y = 0;
        this.slides.forEach((slide) => {
          const image = slide.querySelector('[data-lightbox-image]');
          if (!image) return;
          image.style.transform = '';
          image.style.transformOrigin = '';
          image.classList.remove('is-zoomed');
        });
      }

      onImageClick(event) {
        if (!this.desktop.matches) return;

        if (this.zoom.scale > 1) {
          this.resetZoom();
          return;
        }

        this.zoom.scale = Number(this.dataset.desktopZoom) || 1.8;
        this.setOriginFromPointer(event);
        this.applyZoom(event.currentTarget);
      }

      onImageMouseMove(event) {
        if (!this.desktop.matches || this.zoom.scale <= 1) return;
        this.setOriginFromPointer(event);
      }

      setOriginFromPointer(event) {
        const image = event.currentTarget;
        const rect = image.getBoundingClientRect();
        const x = Math.min(Math.max(((event.clientX - rect.left) / rect.width) * 100, 0), 100);
        const y = Math.min(Math.max(((event.clientY - rect.top) / rect.height) * 100, 0), 100);
        image.style.transformOrigin = `${x}% ${y}%`;
      }

      /* Touch: pinch zoom, drag to pan, swipe to change slide */

      distance(touches) {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
      }

      onTouchStart(event) {
        if (event.touches.length === 2) {
          event.preventDefault();
          this.zoom.pinching = true;
          this.zoom.startDistance = this.distance(event.touches);
          this.zoom.startScale = this.zoom.scale;
          return;
        }

        const touch = event.touches[0];
        this.touch.startX = touch.clientX;
        this.touch.currentX = touch.clientX;
        this.touch.lastX = touch.clientX;
        this.touch.lastY = touch.clientY;
        this.touch.dragging = true;
      }

      onTouchMove(event) {
        const image = this.currentImage();

        if (event.touches.length === 2 && this.zoom.pinching) {
          event.preventDefault();
          const ratio = this.distance(event.touches) / this.zoom.startDistance;
          this.zoom.scale = Math.min(Math.max(this.zoom.startScale * ratio, 1), 4);
          this.applyZoom(image);
          return;
        }

        if (event.touches.length !== 1 || !this.touch.dragging) return;

        const touch = event.touches[0];

        if (this.zoom.scale > 1) {
          event.preventDefault();
          this.zoom.x += touch.clientX - this.touch.lastX;
          this.zoom.y += touch.clientY - this.touch.lastY;
          this.touch.lastX = touch.clientX;
          this.touch.lastY = touch.clientY;
          this.applyZoom(image);
          return;
        }

        this.touch.currentX = touch.clientX;
      }

      onTouchEnd(event) {
        if (event.touches.length < 2) this.zoom.pinching = false;

        const finished = event.touches.length === 0;

        if (finished && this.zoom.scale <= 1 && this.touch.dragging) {
          const delta = this.touch.startX - this.touch.currentX;
          if (Math.abs(delta) > 50) this.go(delta > 0 ? this.index + 1 : this.index - 1);
        }

        if (finished) {
          const now = Date.now();
          const sinceLastTap = now - this.touch.lastTap;
          const moved = Math.abs(this.touch.startX - this.touch.currentX) > 10;

          if (!moved && sinceLastTap > 0 && sinceLastTap < 300) {
            if (this.zoom.scale > 1) {
              this.resetZoom();
            } else {
              this.zoom.scale = Number(this.dataset.mobileZoom) || 2.5;
              this.zoom.x = 0;
              this.zoom.y = 0;
              this.applyZoom();
            }
          }

          this.touch.lastTap = now;
          this.touch.dragging = false;
        }

        if (this.zoom.scale <= 1) {
          this.zoom.x = 0;
          this.zoom.y = 0;
          this.applyZoom();
        }
      }
    }
  );
}
